import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import JobConfigPageService from 'src/services/jobConfigPageService';
import JobsService, { type JobListItem } from 'src/services/jobsService';
import { arePrimitiveValuesDiff } from 'src/utils/helpers';

import { jobPayloadToTableRow } from '../createNewJobSlice';

import { initialState } from './constants';
import { type FilterTag, type JobConfigTableDataRow } from './types';

// **** Setup **** //

/**
 * Get all jobs
 */
export const getJobs = createAsyncThunk('jobs/getJobs', async () => {
  const { data } = await JobsService.getJobs();
  return data;
});

/**
 * Add or edit a job
 */
export const addOrEditJob = createAsyncThunk(
  'jobs/createOrUpdateJobs',
  async (job: JobConfigTableDataRow, { getState }) => {
    const state = ((await getState()) as any).createNewJobPage;
    const { data } = await JobsService.createOrUpdateJob(
      JobConfigPageService.tableRowToJobPayload(job, state.regions, state.types, state.siteTypes, state.specificSites),
    );
    return jobPayloadToTableRow(data);
  },
);

/**
 * Get applicable-sites
 */
export const getApplicableSites = createAsyncThunk('jobs/getApplicableSites', async (jobId: number) => {
  const { data } = await JobsService.getApplicableSitesForJob(jobId);
  return data;
});

// Job Config Page Slice
const { reducer, actions } = createSlice({
  name: 'jobConfigPageSlice',
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setDisplayedApplicableSites: (state, action) => {
      state.displayedApplicableSites = action.payload;
    },
    setCreatedJobSuccessfully: (state, action) => {
      state.createdJobSuccessfully = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setIsJobConfigFilterSheetOpen: (state, action) => {
      state.isJobConfigTableFilterSheetOpen = action.payload;
    },
    setJobConfigTableFilter: (state, action) => {
      state.jobConfigTableFilter = action.payload;
      state.jobConfigTableFilterTags = JobConfigPageService.getAppliedFilterTagsFromJobConfigTableFilter(
        state.jobConfigTableFilter,
      );
    },
    removeTagAndUpdateFilter: (state, action) => {
      const newFilter = { ...state.jobConfigTableFilter };
      const filterTag: FilterTag = action.payload;
      const { optionName, value } = filterTag;
      newFilter[optionName] = newFilter[optionName].filter((item) => arePrimitiveValuesDiff(item, value));
      state.jobConfigTableFilter = newFilter;
      const tags = JobConfigPageService.getAppliedFilterTagsFromJobConfigTableFilter(newFilter);
      state.jobConfigTableFilterTags = tags;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJobs.fulfilled, (state, { payload }) => {
      state.tableData = payload.map((job: JobListItem) => JobConfigPageService.jobListItemToTableRow(job));
      state.isLoading = false;
      state.jobConfigTableFilterOptions = JobConfigPageService.setupJobConfigFilterOptionsFromTableRows(
        state.tableData,
      );
    });
    builder.addCase(addOrEditJob.fulfilled, (state, { payload }) => {
      if (payload) {
        state.searchField = payload.name;
        const isAlreadyInTable = state.tableData.some((row) => row.id === payload.id);
        // This will be the case when editing an existing site
        if (isAlreadyInTable) {
          state.tableData = state.tableData.map((row) => (row.id === payload.id ? payload : row));
          // This will be the case when adding a new site
        } else {
          state.tableData = [...state.tableData, payload];
        }
        state.createdJobSuccessfully = true;
      }
    });
    builder.addCase(getApplicableSites.fulfilled, (state, { payload }) => {
      state.displayedApplicableSites = payload;
    });
  },
});

// **** Export **** //

export const {
  setIsLoading,
  setSearchField,
  setCurrentPage,
  setTotalPages,
  setDisplayedApplicableSites,
  setCreatedJobSuccessfully,
  setPerPage,
  setIsJobConfigFilterSheetOpen,
  setJobConfigTableFilter,
  removeTagAndUpdateFilter,
} = actions;

export default reducer;
